<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.owner_id"
          placeholder="Select Owner Name"
          class="px-2"
          :options="ownerOptions"
        />
      </vs-col>
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.route_name"
          placeholder="Select Route Name"
          class="px-2"
          :options="routeOptions"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.month"
          :format="'MMM'"
          type="month"
          value-type="M"
          placeholder="Month"
          class="px-2"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.year"
          :format="'YYYY'"
          type="year"
          value-type="YYYY"
          placeholder="Year"
          class="px-2"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>
    <div class="p-4 bg-white rounded-lg shadow mt-4">
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <table
        v-else
        class="min-w-full text-sm text-left"
      >
        <thead>
          <tr class="font-semibold">
            <th class="w-1/6 py-2 px-4 text-center">
              Summary
            </th>
            <th class="w-1/6" />
            <th class="w-1/6" />
            <th
              class="py-2 px-4 text-center"
              colspan="6"
            >
              Actual
            </th>
          </tr>
          <tr class="border-b">
            <th class="" />
            <th class="px-4 text-center">
              Target
            </th>
            <th class="px-4 text-center">
              {{ filters.year }}YTD
            </th>
            <th
              v-for="(name, i) in headerMonths"
              :key="i"
              class="px-4 text-center"
            >
              {{ name }}
            </th>
            <th class="px-4 text-center">
              MTD
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in tableData"
            :key="idx"
            class="border-b text-center"
          >
            <td class="">
              {{ row.label }}
            </td>
            <td class="">
              {{ handleValidateAndFormatNumber(row.target) }}%
            </td>
            <td class="">
              <div
                class="h-24 text-white rounded mx-2 px-2 py-1 text-center font-bold flex flex-col items-center justify-center"
                :style="{ backgroundColor: row.difference < 0 ? '#FF3662' : '#33B57D' }"
              >
                <div class="">
                  {{ handleValidateAndFormatNumber(row.ytd) }}%
                </div>
                <div
                  class="mt-3 text-xs px-2 py-1 rounded inline-block"
                  :class="row.difference < 0 ? 'bg-pink-100 text-pink-700' : 'bg-green-100 text-green-700'"
                >
                  {{ row.difference < 0 ? '-' : '+' }}{{ Math.abs(row.difference).toFixed(2) }}%
                </div>
              </div>
            </td>
            <td
              :colspan="row.monthly ? row.monthly.length : 1"
            >
              <line-chart
                :data="generateLineData(
                  row.difference < 0 ? '#FF3662' : '#33B57D',
                  row.monthly,
                  row.target
                )"
                :options="chartSummaryOptions"
                :plugins="chartPlugins.line"
                class="h-24"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- POL -->
    <div class="mt-4 bg-white shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold mb-4">
        On-Time Performance POL
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="flex flex-col md:flex-row gap-4"
      >
        <!-- Left Panel -->
        <div class="border rounded-lg p-4 w-full md:w-1/4">
          <div class="text-center space-y-2 h-full flex flex-col">
            <p class="text-sm font-medium">
              Avg. % On-Time POL
            </p>
            <div class="h-full m-auto flex">
              <div class="self-center">
                <div
                  class="text-3xl font-bold text-white py-2 px-4 rounded-md"
                  :style="{backgroundColor: polDetail.avg_data.actual < polDetail.avg_data.target ? '#FF3662' : '#33B57D'}"
                >
                  {{ handleValidateAndFormatNumber(polDetail.avg_data.actual) }}%
                </div>
                <div class="mt-2 text-xs font-semibold">
                  TARGET <span class="text-indigo-500">{{ handleValidateAndFormatNumber(polDetail.avg_data.target) }}%</span>
                </div>
              </div>
            </div>
            
            <div class="border-t border-dashed border-gray-300 my-4" />
            <p class="text-sm">
              <span class="font-bold text-danger">
                {{ polDetail.total_trip - (polDetail.total_trip * polDetail.avg_data.actual / 100).toFixed(0) }}
              </span> out of
              <span class="font-bold text-danger">{{ polDetail.total_trip }}</span> trips are
              <span class="text-danger font-semibold">late</span>
            </p>
          </div>
        </div>

        <!-- Right Panel -->
        <div class="w-full md:w-3/4 h-72">
          <BarChart
            :data="polDetail.bar_chart_data"
            :plugins="chartPlugins.bar"
            :options="chartOnTimeOptions"
          />
        </div>
      </div>
    </div>
    <div class="mt-4 bg-white shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold mb-4">
        Delay Time of Waiting Barge (POL)
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="flex flex-col md:flex-row gap-4 items-center"
      >
        <div class="p-4 w-full md:w-1/3">
          <DoughnutChart
            :data="polDetail.delay_doughtnut_chart_data"
            :plugins="chartPlugins.doughnut"
            :options="chartDoughtnutOptions"
          />
        </div>
        <div class="w-full md:w-2/3">
          <BarChart
            :data="polDetail.delay_bar_chart_data"
            :plugins="chartPlugins.bar"
            :options="chartDelayOptions"
          />
        </div>
      </div>
    </div>
    <!-- POD -->
    <div class="mt-4 bg-white shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold mb-4">
        On-Time Performance POD
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="flex flex-col md:flex-row gap-4"
      >
        <!-- Left Panel -->
        <div class="border rounded-lg p-4 w-full md:w-1/4">
          <div class="text-center space-y-2 h-full flex flex-col">
            <p class="text-sm font-medium">
              Avg. % On-Time POD
            </p>
            <div class="h-full m-auto flex">
              <div class="self-center">
                <div
                  class="text-3xl font-bold text-white py-2 px-4 rounded-md"
                  :style="{backgroundColor: podDetail.avg_data.actual < podDetail.avg_data.target ? '#FF3662' : '#33B57D'}"
                >
                  {{ handleValidateAndFormatNumber(podDetail.avg_data.actual) }}%
                </div>
                <div class="mt-2 text-xs font-semibold">
                  TARGET <span class="text-indigo-500">{{ handleValidateAndFormatNumber(podDetail.avg_data.target) }}%</span>
                </div>
              </div>
            </div>
            
            <div class="border-t border-dashed border-gray-300 my-4" />
            <p class="text-sm">
              <span class="font-bold text-danger">
                {{ podDetail.total_trip - (podDetail.total_trip * podDetail.avg_data.actual / 100).toFixed(0) }}
              </span> out of
              <span class="font-bold text-danger">{{ podDetail.total_trip }}</span> trips are
              <span class="text-danger font-semibold">late</span>
            </p>
          </div>
        </div>

        <!-- Right Panel -->
        <div class="w-full md:w-3/4 h-72">
          <BarChart
            :data="podDetail.bar_chart_data"
            :plugins="chartPlugins.bar"
            :options="chartOnTimeOptions"
          />
        </div>
      </div>
    </div>
    <div class="mt-4 bg-white shadow rounded-lg p-4">
      <h3 class="text-lg font-semibold mb-4">
        Delay Time of Waiting Barge (POD)
      </h3>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="flex flex-col md:flex-row gap-4 items-center"
      >
        <div class="p-4 w-full md:w-1/3">
          <DoughnutChart
            :data="podDetail.delay_doughtnut_chart_data"
            :plugins="chartPlugins.doughnut"
            :options="chartDoughtnutOptions"
          />
        </div>
        <div class="w-full md:w-2/3">
          <BarChart
            :data="podDetail.delay_bar_chart_data"
            :plugins="chartPlugins.bar"
            :options="chartDelayOptions"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {isEmpty} from "lodash";
import {mapGetters} from "vuex";

import BarChart from "@/components/barChart"
import DatePicker from '@/components/datePicker'
import DoughnutChart from "@/components/doughnutChart"
import LineChart from "@/components/lineChart/index.vue";
import SelectWithSearch from "@/components/selectWithSearch";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  components: {
    LineChart,
    DatePicker,
    BarChart,
    DoughnutChart,
    SelectWithSearch
  },
  data() {
    const currentYear = this.moment().format('YYYY')
    return {
      isBusy: true,
      usedColors: new Set(),
      filters: {
        owner_id: null,
        route_name: null,
        month: null,
        year: currentYear
      },
      ownerOptions: [],
      routeOptions: [],
      headerMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
      tableData: [
        {
          label: '% On-Time POL',
          target: 0,
          ytd: 0,
          difference: 0,
          months: []
        },
        {
          label: '% On-Time POD',
          target: 0,
          ytd: 0,
          difference: 0,
          months: []
        }
      ],
      polDetail: {
        total_trip: 0,
        avg_data: {
          actual: 0,
          target: 0
        },
        bar_chart_data: {
          labels: ['JAN'],
          datasets: [
            {
              label: 'On-Time',
              data: [0],
              backgroundColor: '#7B61FF',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              label: 'Late',
              data: [0],
              backgroundColor: '#FF3662',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        },
        delay_doughtnut_chart_data: {
          labels: [
            ''
          ],
          datasets: [{
            data: [100],
            backgroundColor: ['#4ADE80'],
            borderWidth: 1
          }]
        },
        delay_bar_chart_data: {
          labels: [''],
          datasets: [
            {
              label: 'Time (hrs)',
              data: [0],
              backgroundColor: '#EE443F',
              borderRadius: 4,
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#FF0000',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        }
      },
      podDetail: {
        total_trip: 0,
        avg_data: {
          actual: 0,
          target: 0
        },
        bar_chart_data: {
          labels: ['JAN'],
          datasets: [
            {
              label: 'On-Time',
              data: [0],
              backgroundColor: '#7B61FF',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              label: 'Late',
              data: [0],
              backgroundColor: '#FF3662',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        },
        delay_doughtnut_chart_data: {
          labels: [
            ''
          ],
          datasets: [{
            data: [100],
            backgroundColor: ['#4ADE80'],
            borderWidth: 1
          }]
        },
        delay_bar_chart_data: {
          labels: [''],
          datasets: [
            {
              label: 'Time (hrs)',
              data: [0],
              backgroundColor: '#EE443F',
              borderRadius: 4,
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#FF0000',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        }
      },
      maxValue: 500
    }
  },
  computed: {
    ...mapGetters({
      owners: 'Dashboard/ownerOpt',
      routes: 'Dashboard/routeOpt',
      onTimePerformanceUtilizationData: 'OnTimePerformanceUtilization/onTimePerformanceUtilizationData'
    }),
    chartPlugins() {
      return {
        doughnut: [
          this.drawPercentagePlugin()
        ],
        bar: [
          this.sumPlugin()
        ],
        line: [
          this.customFillPlugin()
        ]
      }
    },
    chartOnTimeOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: context =>
                `${context.dataset.label}: ${context.parsed.y} trips`
            }
          }
        },
        scales: {
          x: { stacked: true },
          y: {
            stacked: true,
            title: {
              display: true,
              text: 'Trips'
            },
            min: 0,
            max: this.maxValue // set your max value here
          }
        }
      }
    },
    chartDoughtnutOptions() {
      return {
        responsive: true,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => `${tooltipItem.label}: ${tooltipItem.raw}%`
            }
          }
        },
        cutout: '60%'
      }
    },
    chartDelayOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 30 } },
        scales: {
          x: {
            ticks: {
              font: { size: 10 },
              callback: function(value, index) {
                return this.getLabelForValue(index).split('\n')
              }
            },
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Time (hrs)'
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => `${ctx.dataset.label}: ${ctx.raw} hrs`
            }
          }
        }
      }
    },
    chartSummaryOptions() {
      return {
        responsive: true,
        stacked: false,
        maintainAspectRatio: false,
        layout: { padding: { top: 30 } },
        scales: {
          x: {
            display: false,
            offset: true
          },
          y: {
            type: 'linear',
            display: false,
            offset: true,
            grid: {
              drawOnChartArea: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        }
      }
    }
  },
  watch: {
    owners: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.ownerOptions = val.map(item => ({ text: item.owner_name, value: item.id }))
        }
      }
    },
    routes: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.routeOptions = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    onTimePerformanceUtilizationData: {
      handler(val) {
        if (!this.isEmpty(val)) {
          // Populate Data Summary
          this.headerMonths = this.populateDataMonth(val?.on_time_pod.monthly?.length - 1)
          this.tableData = [val?.on_time_pol, val?.on_time_pod]
          this.tableData[0].label = '% On-Time POL'
          this.tableData[1].label = '% On-Time POD'

          // Populate Data Detail
          this.polDetail = this.generateDetailData(val.pol_detail)
          this.podDetail = this.generateDetailData(val.pod_detail)
        } else {
          this.resetState()
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        if (val.month || val.year) {
          this.fetchOwners()
          this.fetchRoutes()
        }
        this.fetchOnTimePerformanceUtilization()
      }
    }
  },
  mounted() {
    this.fetchOnTimePerformanceUtilization()
    this.fetchOwners()
    this.fetchRoutes()
  },
  methods: {
    generateDetailData(data) {
      if(!data) return
      const tempDetail = this.cloneDeep(data)

      this.maxValue = Math.max(
        ...tempDetail?.bar_chart_data?.map(v => v?.on_time),
        ...tempDetail?.bar_chart_data?.map(v => v?.late),
        ...tempDetail?.bar_chart_data?.map(v => v?.target)
      ) + 100

      return {
        total_trip: tempDetail?.bar_chart_data?.reduce((sum, item) => sum += item?.total, 0),
        avg_data: tempDetail?.avg_data,
        bar_chart_data: {
          labels: this.populateDataMonth(tempDetail?.bar_chart_data?.length, tempDetail?.bar_chart_data?.[0]?.month),
          datasets: [
            {
              label: 'On-Time',
              data: tempDetail?.bar_chart_data?.map(v => v?.on_time),
              backgroundColor: '#7B61FFBF',
              barPercentage:0.6,
              categoryPercentage: 0.6,
              order: 1
            },
            {
              label: 'Late',
              data: tempDetail?.bar_chart_data?.map(v => v?.late),
              backgroundColor: '#FF3662BF',
              barPercentage:0.6,
              categoryPercentage: 0.6,
              order: 1
            },
            {
              type: 'line',
              label: 'Target',
              data: tempDetail?.bar_chart_data?.map(v => v?.target),
              borderColor: '#FF0000',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 5,
              order: 0
            }
          ]
        },
        delay_doughtnut_chart_data: {
          labels: tempDetail?.delay_chart?.map(v => v?.reason ?? ''),
          datasets: [{
            data: tempDetail?.delay_chart?.map(v => v?.percentage),
            backgroundColor: tempDetail?.delay_chart?.map(_ => this.getRandomColor(1)),
            borderWidth: 1
          }]
        },
        delay_bar_chart_data: {
          labels: tempDetail?.delay_chart?.map(v => v?.reason ?? ''),
          datasets: [
            {
              label: 'Time (hrs)',
              data: tempDetail?.delay_chart?.map(v => v?.duration),
              backgroundColor: '#EE443FBF',
              borderRadius: 4,
              barPercentage:0.6,
              categoryPercentage: 0.6,
              order: 1
            },
            {
              type: 'line',
              label: 'Target',
              data: tempDetail?.delay_chart?.map(v => v?.target),
              borderColor: '#FF0000',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 5,
              order: 0
            }
          ]
        }
      }
    },
    async fetchOnTimePerformanceUtilization() {
      this.isBusy = true
      try {
        const apiFilters = Object.fromEntries(
          Object.entries(this.filters || {}).filter(([_, value]) => !!value)
        )
        await this.$store.dispatch('OnTimePerformanceUtilization/fetchOnTimePerformanceUtilization', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to get On-Time Performance Utilization Data, please try again')
      }
    },
    async fetchOwners() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getOwnerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getRouteOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    populateDataMonth(length, startFrom = 1) {
      if (!length) {
        return []
      }
      return Array.from({ length }, (_, i) =>
        this.moment().month((startFrom - 1 + i) % 12).format('MMM')
      )
    },
    getRandomColor(alpha = 0.7) {
      let color;
      do {
        const r = Math.floor(Math.random() * 156) + 100;
        const g = Math.floor(Math.random() * 156) + 100;
        const b = Math.floor(Math.random() * 156) + 100;
        color = `rgba(${r},${g},${b},${alpha})`;
      } while (this.usedColors.has(color));

      this.usedColors.add(color);
      return color;
    },
    drawPercentagePlugin() {
      return {
        id: 'drawPercentage',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index];
            const calculated = ((value / total) * 100).toFixed(1)
            const percentage = `${calculated}%`;

            const {x, y} = arc.tooltipPosition(); // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, x, y);
            ctx.restore();
          });
        }
      }
    },
    sumPlugin() {
      return {
        afterDraw: function(chart) {
          const ctx = chart.ctx;
          ctx.save();
          const datasets = chart.data.datasets;
          const metaArr = chart.getSortedVisibleDatasetMetas();
          const lastMetaIndex = metaArr.length - 1;

          chart.data.labels.forEach((_, dataIndex) => {
            let sum = 0;
            datasets.forEach(ds => {
              sum += ds.type === 'line' ? 0 : ds.data[dataIndex];
            });
            const topBar = metaArr[lastMetaIndex].data[dataIndex];
            if (topBar) {
              ctx.font = 'bold 12px Poppins';
              ctx.fillStyle = '#222';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillText(sum, topBar.x, topBar.y - 6);
            }
          });
          ctx.restore();
        }
      };
    },
    resetFilters() {
      this.filters = {
        owner_id: null,
        route_name: null,
        month: null,
        year: this.moment().format('YYYY')
      }
    },
    generateLineData(color, data, baseline = null) {
      if(!data) return
      const formattedData = data.map((value) => ( handleValidateAndFormatNumber(value)))
      if (baseline < 0) {
        const avg = data.reduce((a, b) => a + b, 0) + 0.25 / data.length
        baseline = Array(formattedData.length).fill(avg)
      } else {
        baseline = Array(formattedData.length).fill(baseline)
      }

      return {
        labels: Array(data.length).fill(''),
        datasets: [
          {
            data: formattedData,
            fill: false,
            borderWidth: 2,
            borderColor: color,
            borderCapStyle: 'round',
            pointRadius: 4,
            pointBackgroundColor: color
          },
          {
            ...(!!baseline && {
              data: baseline,
              borderWidth: 1,
              borderColor: '#7889DA',
              borderDash: [3, 3],
              pointRadius: 0
            })
          }
        ]
      }
    },
    customFillPlugin() {
      return {
        id: 'customFill',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { left, right, top, bottom }, scales: { x, y } } = chart
          const meta = chart.getDatasetMeta(0)
          const points = meta.data
          const lastIndex = points.length - 1

          ctx.fillStyle = '#F8F8F8'
          ctx.fillRect(
            0,
            0,
            points[lastIndex].x - x.left,
            chart.height
          )

          const pointDistance = ((right - left) / (meta.data.length - 1)) / 2
          const blueFillStartX = points[lastIndex].x - pointDistance
          ctx.fillStyle = '#E0ECFB'
          ctx.fillRect(
            blueFillStartX,
            0,
            x.right - blueFillStartX,
            chart.height
          )
        }
      }
    },
    resetState() {
      this.headerMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May']
      this.tableData = [
        {
          label: '% On-Time POL',
          target: 0,
          ytd: 0,
          difference: 0,
          months: []
        },
        {
          label: '% On-Time POD',
          target: 0,
          ytd: 0,
          difference: 0,
          months: []
        }
      ]
      this.polDetail = {
        total_trip: 0,
        avg_data: {
          actual: 0,
          target: 0
        },
        bar_chart_data: {
          labels: ['JAN'],
          datasets: [
            {
              label: 'On-Time',
              data: [0],
              backgroundColor: '#7B61FF',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              label: 'Late',
              data: [0],
              backgroundColor: '#FF3662',
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        },
        delay_doughtnut_chart_data: {
          labels: [
            ''
          ],
          datasets: [{
            data: [100],
            backgroundColor: ['#4ADE80'],
            borderWidth: 1
          }]
        },
        delay_bar_chart_data: {
          labels: [''],
          datasets: [
            {
              label: 'Time (hrs)',
              data: [0],
              backgroundColor: '#EE443F',
              borderRadius: 4,
              barPercentage:0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#FF0000',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        }
      },
      this.podDetail = {
        total_trip: 0,
        avg_data: {
          actual: 0,
          target: 0
        },
        bar_chart_data: {
          labels: ['JAN'],
          datasets: [
            {
              label: 'On-Time',
              data: [0],
              backgroundColor: '#7B61FF',
              barPercentage: 0.6,
              categoryPercentage: 0.6
            },
            {
              label: 'Late',
              data: [0],
              backgroundColor: '#FF3662',
              barPercentage: 0.6,
              categoryPercentage: 0.6
            },
            {
              type: 'line',
              label: 'Target',
              data: [0],
              borderColor: '#ef4444',
              borderWidth: 2,
              borderDash: [8, 6],
              fill: false,
              pointRadius: 0
            }
          ]
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}
</style>
